import {
    AlertTriangle,
    ArrowLeft,
    Inbox,
    ListPlus,
    MessageSquare,
    Pencil,
    SlidersHorizontal,
    Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom-v5-compat';
import { toast } from 'sonner';

import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Tabs } from '../../components/Tabs';
import { ROLE_LEVELS, useCurrentUser } from '../../hooks/useCurrentUser';
import { cn } from '../../lib/cn';
import { formatDateRO } from '../../lib/format';

import { AttachmentsPanel } from './components/AttachmentsPanel';
import { CommentComposer } from './components/CommentComposer';
import { LabelsPanel } from './components/LabelsPanel';
import { ReferencesPanel } from './components/ReferencesPanel';
import { TicketAssigneeChanger } from './components/TicketAssigneeChanger';
import { TicketFormDialog } from './components/TicketFormDialog';
import { TicketPriorityBadge } from './components/TicketPriorityBadge';
import { TicketStatusBadge } from './components/TicketStatusBadge';
import { TicketStatusChanger } from './components/TicketStatusChanger';
import { TicketTimeline } from './components/TicketTimeline';
import { ticketErrorRO } from './ticketCopy';
import {
    useBacklogTicketMutation,
    useDeleteTicketMutation,
    useGetTicketQuery,
    useRestoreTicketMutation,
} from './tickets.api';
import { isOverdue } from './tickets.schema';

type DetailTab = 'activity' | 'details';

// Toggle a ticket between the active board and the backlog. Backlogged tickets
// get the "Activeaza" (restore) action; everything else can be sent to backlog.
function BacklogAction({ id, backlogged }: { id: number; backlogged: boolean }) {
    const [backlog, { isLoading: sending }] = useBacklogTicketMutation();
    const [restore, { isLoading: restoring }] = useRestoreTicketMutation();
    const busy = sending || restoring;

    const onClick = async () => {
        try {
            if (backlogged) {
                await restore(id).unwrap();
                toast.success('Ticket mutat in lista activa');
            } else {
                await backlog(id).unwrap();
                toast.success('Ticket trimis in backlog');
            }
        } catch (e) {
            toast.error(ticketErrorRO(e, 'Actiunea nu a reusit.'));
        }
    };

    return (
        <Button variant="secondary" size="lg" className="w-full" onClick={onClick} disabled={busy}>
            {backlogged ? (
                <ListPlus aria-hidden="true" className="h-5 w-5 shrink-0" />
            ) : (
                <Inbox aria-hidden="true" className="h-5 w-5 shrink-0" />
            )}
            {backlogged ? 'Activeaza ticketul' : 'Trimite in backlog'}
        </Button>
    );
}

// The full detail view of one ticket — everything below the page's back-link.
// No router coupling, so it can be hosted in a dialog (entity ticket lists)
// as well as on the standalone page: the host decides what a delete means.
// `narrow` forces the mobile single-column/tabs layout regardless of viewport —
// for hosts like the board's preview column, where lg: breakpoints would
// squeeze the desktop three-column grid into a ~440px panel.
export function TicketDetailBody({
    ticketId,
    onDeleted,
    narrow = false,
}: {
    ticketId: number;
    onDeleted?: () => void;
    narrow?: boolean;
}) {
    const [editing, setEditing] = useState(false);
    const [deleting, setDeleting] = useState(false);
    // Mobile only: collapse the long body into two tabs. Desktop shows both
    // columns at once (lg: overrides the hidden/block toggles below).
    const [tab, setTab] = useState<DetailTab>('activity');
    const [deleteTicket] = useDeleteTicketMutation();

    // Deletion is Manager / Administrator only — shop-floor roles (Staff, Shop)
    // can work tickets but not remove them. Mirror of the API's DELETE gate.
    const user = useCurrentUser();
    const canDelete =
        !!user &&
        (user.role?.level ?? 0) >= ROLE_LEVELS.MANAGER &&
        !['Staff', 'Shop'].includes(user.role?.name ?? '');

    const { data: ticket, isLoading, error } = useGetTicketQuery(ticketId, {
        skip: !Number.isFinite(ticketId),
    });

    const onDelete = async () => {
        try {
            await deleteTicket(ticketId).unwrap();
            toast.success('Ticketul a fost sters.');
            onDeleted?.();
        } catch (e) {
            toast.error(ticketErrorRO(e, 'Eroare la stergere.'));
        }
    };

    if (isLoading) {
        return <p className="text-base text-gray-500 dark:text-gray-400">Se incarca...</p>;
    }

    if (error || !ticket) {
        return (
            <p className="text-base text-red-600 dark:text-red-400">
                {error ? ticketErrorRO(error, 'Eroare la incarcarea ticketului.') : 'Ticketul nu a fost gasit.'}
            </p>
        );
    }

    return (
        <>
            {/* Summary + the primary status action, grouped into a single
                card so the top of the page reads as one block instead of
                three stacked ones. */}
            <div className="rounded-2xl border border-solid border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <div className="flex items-start justify-between gap-3 p-4 sm:p-5">
                    <div className="min-w-0">
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                            <TicketStatusBadge status={ticket.status} size="md" />
                            <TicketPriorityBadge priority={ticket.priority} size="md" />
                            {isOverdue(ticket) && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700 ring-1 ring-inset ring-black/5 dark:bg-red-900/40 dark:text-red-200 dark:ring-white/10">
                                    <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5" />
                                    Depasit
                                </span>
                            )}
                        </div>
                        <h1 className="text-xl font-bold leading-tight text-gray-900 dark:text-gray-100 sm:text-2xl">
                            <span className="font-normal text-gray-400 dark:text-gray-500">
                                #{ticket.id}
                            </span>{' '}
                            {ticket.title}
                        </h1>
                        <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                            Creat de {ticket.createdByName || 'necunoscut'}
                            {ticket.dueDate ? ` · termen ${formatDateRO(ticket.dueDate)}` : ''}
                        </p>
                    </div>
                    <Button
                        variant="secondary"
                        className="shrink-0"
                        onClick={() => setEditing(true)}
                        aria-label="Editeaza ticketul"
                    >
                        <Pencil aria-hidden="true" className="h-4 w-4 shrink-0" />
                        <span className="hidden sm:inline">Editeaza</span>
                    </Button>
                </div>

                {ticket.description && (
                    <div className="border-t border-solid border-gray-100 px-4 py-4 dark:border-gray-700/60 sm:px-5">
                        <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700 dark:text-gray-300 sm:text-base">
                            {ticket.description}
                        </p>
                    </div>
                )}

                <div className="border-t border-solid border-gray-100 px-4 py-4 dark:border-gray-700/60 sm:px-5">
                    <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Schimba statusul
                    </h2>
                    <TicketStatusChanger id={ticket.id} status={ticket.status} />
                </div>
            </div>

            {/* Mobile: tabs collapse the long stack. Desktop: both columns
                show side by side and the tab bar is hidden. */}
            <Tabs
                className={cn('mt-6', !narrow && 'lg:hidden')}
                value={tab}
                onValueChange={setTab}
                panelId="ticket-panels"
                tabs={[
                    {
                        value: 'activity',
                        label: 'Activitate',
                        icon: MessageSquare,
                        badge: ticket.events?.length || undefined,
                    },
                    {
                        value: 'details',
                        label: 'Detalii',
                        icon: SlidersHorizontal,
                        badge: ticket.labels?.length || undefined,
                    },
                ]}
            />

            <div
                id="ticket-panels"
                className={cn('mt-4 grid grid-cols-1 gap-6', !narrow && 'lg:mt-6 lg:grid-cols-3')}
            >
                <div
                    className={cn(
                        !narrow && 'lg:col-span-2 lg:block',
                        tab === 'activity' ? 'block' : 'hidden',
                    )}
                >
                    <h2
                        className={cn(
                            'mb-3 hidden text-base font-semibold text-gray-900 dark:text-gray-100',
                            !narrow && 'lg:block',
                        )}
                    >
                        Activitate
                    </h2>
                    <div className="mb-4">
                        <CommentComposer id={ticket.id} />
                    </div>
                    <TicketTimeline events={ticket.events ?? []} />
                </div>

                <aside
                    className={cn(
                        'flex-col gap-6',
                        !narrow && 'lg:flex',
                        tab === 'details' ? 'flex' : 'hidden',
                    )}
                >
                    <section>
                        <h2 className="mb-2 text-base font-semibold text-gray-900 dark:text-gray-100">
                            Responsabil
                        </h2>
                        <TicketAssigneeChanger
                            id={ticket.id}
                            value={
                                ticket.assigneeUserId != null
                                    ? {
                                          assigneeUserId: ticket.assigneeUserId,
                                          assigneeName: ticket.assigneeName ?? '',
                                      }
                                    : null
                            }
                        />
                    </section>
                    <section>
                        <LabelsPanel id={ticket.id} labels={ticket.labels ?? []} />
                    </section>
                    <section>
                        <ReferencesPanel id={ticket.id} />
                    </section>
                    <section>
                        <AttachmentsPanel id={ticket.id} />
                    </section>
                    <section className="border-t border-solid border-gray-200 pt-4 dark:border-gray-700">
                        <BacklogAction id={ticket.id} backlogged={!!ticket.backlogged} />
                    </section>
                    {canDelete && (
                        <section className="border-t border-solid border-gray-200 pt-4 dark:border-gray-700">
                            <Button variant="danger" size="lg" className="w-full" onClick={() => setDeleting(true)}>
                                <Trash2 aria-hidden="true" className="h-5 w-5" />
                                Sterge ticketul
                            </Button>
                        </section>
                    )}
                </aside>
            </div>

            <TicketFormDialog open={editing} onOpenChange={setEditing} ticket={ticket} />
            <ConfirmDialog
                open={deleting}
                onClose={() => setDeleting(false)}
                onConfirm={onDelete}
                title="Sterge ticketul?"
                description="Ticketul si activitatea lui vor fi eliminate. Aceasta actiune nu poate fi anulata usor."
                confirmLabel="Sterge"
                variant="danger"
            />
        </>
    );
}

export default function TicketDetailPage() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const ticketId = Number(id);

    if (!Number.isFinite(ticketId)) return <Navigate to="/v2/tickets" replace />;

    return (
        <div className="mx-auto w-full max-w-5xl px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
            <Link
                to="/v2/tickets"
                className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
            >
                <ArrowLeft aria-hidden="true" className="h-4 w-4" />
                Inapoi la tickete
            </Link>
            <TicketDetailBody ticketId={ticketId} onDeleted={() => navigate('/v2/tickets')} />
        </div>
    );
}
